import { useEffect, useRef, useState } from "react";
import { MessageSquareText, X } from "lucide-react";
import { sendMessage } from "../smsApi";
import { formatPhone, PHONE_LINES } from "../phone";
import { useCannedMessages } from "../useCannedMessages";
import type { SmsContact } from "../types";

const MAX_SMS_LENGTH = 1600;

type Props = {
  recipients: SmsContact[];
  onClose: () => void;
  onSent: () => void;
};

export function BlastComposeModal({ recipients, onClose, onSent }: Props) {
  const [from, setFrom] = useState(PHONE_LINES[0].number);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({
    sent: 0,
    failed: 0,
    total: recipients.length,
  });
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cannedOpen, setCannedOpen] = useState(false);
  const cannedMessages = useCannedMessages();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && !sending) onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, sending]);

  function insertCanned(msg: string) {
    setBody(msg);
    setCannedOpen(false);
    bodyRef.current?.focus();
  }

  async function handleSend() {
    if (!body.trim()) {
      setError("Message cannot be empty");
      return;
    }
    setError(null);
    setSending(true);
    setProgress({ sent: 0, failed: 0, total: recipients.length });

    let sent = 0;
    let failed = 0;
    for (const contact of recipients) {
      try {
        await sendMessage({
          to: contact.phone_number,
          from,
          body: body.trim(),
        });
        sent++;
      } catch {
        failed++;
      }
      setProgress({ sent, failed, total: recipients.length });
    }

    setDone(true);
    setSending(false);
    onSent();
  }

  const totalProcessed = progress.sent + progress.failed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !sending && onClose()}
      />

      <div className="relative w-full max-w-lg rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-lg font-semibold text-white">Send to Group</h2>
          <button
            type="button"
            onClick={() => !sending && onClose()}
            disabled={sending}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Recipients ({recipients.length})
            </label>
            <div className="max-h-24 overflow-y-auto rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2">
              <div className="flex flex-wrap gap-1.5">
                {recipients.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex items-center gap-1 rounded-full bg-tribal-border px-2 py-0.5 text-[11px] text-gray-200"
                  >
                    {c.name}
                    <span className="text-tribal-muted">
                      {formatPhone(c.phone_number)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              From
            </label>
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              disabled={sending}
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-gray-300 focus:border-tribal-red focus:outline-none disabled:opacity-50"
            >
              {PHONE_LINES.map((line) => (
                <option key={line.number} value={line.number}>
                  {line.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-medium text-tribal-muted">
                Message
              </label>
              {cannedMessages.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCannedOpen((o) => !o)}
                    disabled={sending}
                    className="flex items-center gap-1 rounded-lg border border-tribal-border bg-tribal-bg px-2 py-1 text-[11px] text-tribal-muted transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
                  >
                    <MessageSquareText className="h-3 w-3" />
                    Quick replies
                  </button>
                  {cannedOpen && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-64 overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface shadow-xl">
                      <p className="border-b border-tribal-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-tribal-muted">
                        Quick Replies
                      </p>
                      <div className="max-h-48 divide-y divide-tribal-border overflow-y-auto">
                        {cannedMessages.map((msg, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => insertCanned(msg)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-200 transition-colors hover:bg-white/5"
                          >
                            {msg.length > 80 ? msg.slice(0, 80) + "…" : msg}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
              placeholder="Type your message…"
              rows={4}
              className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none disabled:opacity-50"
            />
            <div
              className={`mt-1 text-right text-[10px] ${
                body.length > MAX_SMS_LENGTH ? "text-red-400" : "text-tribal-muted"
              }`}
            >
              {body.length}/{MAX_SMS_LENGTH}
            </div>
          </div>

          {(sending || done) && (
            <div>
              <div className="mb-1 flex items-center justify-between text-xs text-tribal-muted">
                <span>
                  {done
                    ? `Done — ${progress.sent} sent${
                        progress.failed > 0 ? `, ${progress.failed} failed` : ""
                      }`
                    : `Sending ${totalProcessed} / ${progress.total}…`}
                </span>
                <span className="text-[10px]">
                  {progress.sent > 0 && (
                    <span className="text-green-400">{progress.sent} sent</span>
                  )}
                  {progress.failed > 0 && (
                    <span className="ml-2 text-red-400">
                      {progress.failed} failed
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-tribal-border">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    done && progress.failed > 0 ? "bg-yellow-500" : "bg-tribal-red"
                  }`}
                  style={{
                    width: `${
                      progress.total > 0
                        ? (totalProcessed / progress.total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
          {done ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending || !body.trim() || body.length > MAX_SMS_LENGTH}
                className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
              >
                {sending
                  ? "Sending…"
                  : `Send to ${recipients.length} Contact${
                      recipients.length !== 1 ? "s" : ""
                    }`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
